/**
 * One miner's page.
 *
 * The reason the site exists: a miner types (or clicks) their reward preimage
 * and sees where they stand and how they have been doing. Everything here is
 * public chain data — there is nothing to log into, and the "this one is mine"
 * mark lives only in this browser.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { MinerDetail } from '../api/generated/MinerDetail'
import type { Window as WindowName } from '../api/generated/Window'
import { RequestFailed, fetchMiner } from '../api/rest'
import {
  ago,
  bigNumber,
  hashrate,
  height,
  share,
  shortMiner,
  tooFewBlocks,
  uncertainty,
  zone,
} from '../lib/format'
import { href } from '../lib/routes'
import { usePinnedMiners } from '../lib/store'
import { ShareChart } from './ShareChart'
import { NodePanel } from './NodePanel'

export function MinerPanel({
  chain,
  miner,
  windowName,
}: {
  chain: string
  miner: string
  windowName: WindowName
}) {
  const [detail, setDetail] = useState<MinerDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { isPinned, toggle } = usePinnedMiners()
  const mine = isPinned(miner)

  useEffect(() => {
    const controller = new AbortController()
    setDetail(null)
    setError(null)
    fetchMiner(chain, miner, windowName, controller.signal)
      .then(setDetail)
      .catch((e: unknown) => {
        // An abort is this component being replaced, not a failure to report.
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
    // Re-fetched on window change because the history range follows the window.
  }, [chain, miner, windowName])

  const row = detail?.current ?? null
  const named = row?.attribution === 'telemetry'

  return (
    <>
      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <div>
            <div className="eyebrow">Miner</div>
            <h2 className="panel-title" style={{ marginTop: 4 }}>
              {/* The best identifier available, in the order a reader can use
                them: an attributed node name, else the reward address the miner
                configured, else the preimage the header carries. The full set
                is below regardless. */}
              {named
                ? row.display
                : detail?.address
                  ? `${detail.address.slice(0, 8)}…${detail.address.slice(-4)}`
                  : shortMiner(miner)}
            </h2>
            {/* All three identifiers, because they answer different questions.
              The node name is an inference and may be absent; the address is
              what the miner configured and recognises; the preimage is what the
              block header actually carries and the only one the chain asserts.
              A miner checking "is this me?" needs the address; anyone checking
              our working needs the preimage. */}
            <dl className="identifiers">
              {detail?.address && (
                <>
                  <dt>Rewards</dt>
                  <dd className="numeral">
                    {/* The way through to what was actually paid: this page
                      counts blocks, the account page counts tokens, and the
                      address is the only thing that joins them. */}
                    <Link to={href({ chain, account: detail.address })}>{detail.address}</Link>
                  </dd>
                </>
              )}
              <dt>Preimage</dt>
              <dd className="numeral">{miner}</dd>
            </dl>
          </div>
          {/* No close button: this is a URL somebody navigated to, not a dialog
            that opened over something they were reading. The back button and
            the chain chip both already do what it would. */}
          <button
            className="segmented panel-button"
            style={{ color: mine ? 'var(--accent-bright)' : 'var(--text-secondary)' }}
            aria-pressed={mine}
            onClick={() => toggle(miner)}
          >
            {mine ? '★ Yours' : '☆ Mark as yours'}
          </button>
        </div>

        {error && <p className="empty">{error}</p>}

        {!error && !detail && <p className="empty">Reading the record…</p>}

        {detail && (
          <>
            <div className="stats" style={{ margin: 0, border: 0, borderBottom: 'var(--rule)' }}>
              <div className="stat">
                <div className="stat-label">Rank</div>
                <div className="stat-value">{row ? `#${row.rank}` : '—'}</div>
                <div className="stat-note">
                  {row ? `${share(row.share)} of the window` : 'no blocks in this window'}
                </div>
              </div>
              <div className="stat">
                <div className="stat-label">Est. hashrate</div>
                <div
                  className={
                    tooFewBlocks(row?.hashrate_relative_error)
                      ? 'stat-value rate-weak'
                      : 'stat-value'
                  }
                >
                  {hashrate(row?.hashrate_estimate)}
                </div>
                {/* The note carries the error bar, because on this page it is
                  the headline number and a bare figure off two blocks is a
                  coin toss with three significant figures. */}
                <div className="stat-note">
                  {uncertainty(row?.hashrate_relative_error)
                    ? `${uncertainty(row?.hashrate_relative_error)} over ${row?.blocks ?? 0} block${row?.blocks === 1 ? '' : 's'}`
                    : 'from work its blocks cost'}
                </div>
              </div>
              <div className="stat">
                <div className="stat-label">Blocks observed</div>
                <div className="stat-value">{height(detail.blocks_observed)}</div>
                <div className="stat-note">
                  {detail.first_seen ? `first seen ${ago(detail.first_seen)}` : 'never seen'}
                </div>
              </div>
              <div className="stat">
                <div className="stat-label">Cumulative work</div>
                <div className="stat-value" title={detail.cumulative_work}>
                  {bigNumber(detail.cumulative_work)}
                </div>
                {/* Difficulty summed over every block won. It does not shrink when
                  difficulty rises, which a block count effectively does. */}
                <div className="stat-note">expected hashes, all blocks</div>
              </div>
              <div className="stat">
                <div className="stat-label">Best streak</div>
                <div className="stat-value">
                  {row && row.best_streak > 1 ? `×${row.best_streak}` : '—'}
                </div>
                <div className="stat-note">consecutive blocks</div>
              </div>
            </div>

            <div style={{ padding: '16px 18px 18px' }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>
                Share of network blocks · times in {zone()}
              </div>
              <ShareChart points={detail.series} />
            </div>
          </>
        )}
      </section>
      {/* A sibling of the miner panel, not a child: the node is a different
          subject with a different provenance, and nesting it put a panel border
          inside a panel border. */}
      {detail?.node && (
        <NodePanel
          node={detail.node}
          confidence={row?.confidence ?? null}
          votes={row?.attribution_votes ?? null}
          attribution={row?.attribution ?? null}
          chain={chain}
        />
      )}
    </>
  )
}
